import { twApi } from './../api';
import { BaseQueryParams } from './../../Global.model';

import {
  Equipment,
  CreateEquipmentParams,
  UpdateEquipmentParams,
  LatestEquipmentReadingInfo,
  LastKnownLocationParams,
  EquipmentFilterParams
} from './equipment.model';
import { LoadLocation } from '../load/load.model';
import { SearchParams } from '..';

/**
 * Api endpoints for equipment entity
 */
const equipmentApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getEquipments: builder.query<{ equipments: Equipment[]; total: number }, BaseQueryParams>({
      query: (arg) => ({
        url: '/equipment/search',
        params: { ...arg },
        method: 'POST'
      }),
      // Updated transform to handle the actual response structure
      transformResponse: (response: {
        success: boolean;
        data: Equipment[];
        total: number;
        _links: any;
        more_records: boolean;
        size: number;
      }) => {
        if (!response.success) {
          throw new Error('Failed to fetch equipment');
        }
        return {
          equipments: response.data,
          total: response.total
        };
      },
      transformErrorResponse: (response) => {
        console.error('Equipment API Error:', response);
        return response;
      },
      providesTags: (result) =>
        result?.equipments
          ? [
              ...result.equipments.map(({ id }) => ({ type: 'Equipment' as const, id })),
              { type: 'Equipment', id: 'EQUIPMENTS_LIST' }
            ]
          : [{ type: 'Equipment', id: 'EQUIPMENTS_LIST' }]
    }),
    searchEquipments: builder.query<
      { equipments: Equipment[]; total: number },
      SearchParams<EquipmentFilterParams>
    >({
      query: (searchEquipmentParam) => {
        const { filters, params } = searchEquipmentParam;

        return {
          url: 'equipment/search',
          method: 'POST',
          body: filters,
          params: params
        };
      },
      transformResponse: (response: {
        success: boolean;
        data: Equipment[];
        total: number;
        _links: any;
        more_records: boolean;
        size: number;
      }) => {
        if (!response.success) {
          throw new Error('Failed to fetch equipment');
        }
        return {
          equipments: response.data,
          total: response.total
        };
      },
      transformErrorResponse: (response) => {
        console.error('Equipment API Error:', response);
        return response;
      },
      providesTags: (result) =>
        result?.equipments
          ? [
              ...result.equipments.map(({ id }) => ({ type: 'Equipment' as const, id })),
              { type: 'Equipment', id: 'EQUIPMENTS_LIST' }
            ]
          : [{ type: 'Equipment', id: 'EQUIPMENTS_LIST' }]
    }),
    getEquipmentById: builder.query<Equipment, string>({
      query: (id) => {
        if (!id) throw new Error('Equipment id required');
        return {
          url: `/equipment/${id}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: Equipment }) => {
        return response.data;
      },
      providesTags: (result, error, id) => [{ type: 'Equipment' as const, id }]
    }),
    addEquipment: builder.mutation<Equipment, CreateEquipmentParams>({
      query: (arg) => ({
        url: '/equipment',
        body: { ...arg },
        method: 'POST'
      }),
      transformResponse: (response: { data: Equipment }) => response.data,
      invalidatesTags: [{ type: 'Equipment', id: 'EQUIPMENTS_LIST' }]
    }),
    updateEquipment: builder.mutation<Equipment, UpdateEquipmentParams>({
      query: (params) => {
        const { id, ...updateParams } = params;
        return {
          url: `/equipment/${id}`,
          method: 'PUT',
          body: { ...updateParams }
        };
      },
      invalidatesTags: (result, error, data) => [
        { type: 'Equipment', id: data.id },
        { type: 'Equipment', id: 'EQUIPMENTS_LIST' }
      ]
    }),
    deleteEquipment: builder.mutation<Equipment, string>({
      query: (id) => ({
        url: `/equipment/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          equipmentApi.util.updateQueryData('getEquipments', {}, (equipmentData) => {
            const index = equipmentData.equipments.findIndex((e) => e.id === id);
            delete equipmentData.equipments[index];
            equipmentData.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Equipment', id },
        { type: 'Equipment', id: 'EQUIPMENTS_LIST' }
      ]
    }),
    getPublicLoadEquipment: builder.query<Equipment, { token: string; equipmentId: string }>({
      query: (params) => {
        const { token, equipmentId } = params;
        return {
          url: `orders/t/${token}/equipment/${equipmentId}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: Equipment }) => {
        return response.data;
      }
    }),
    getAllEquipmentByToken: builder.query<{ equipments: Equipment[]; total: number }, string>({
      query: (token) => {
        return {
          url: `orders/t/${token}/equipment`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: Equipment[] }) => {
        return {
          equipments: response.data,
          total: response.data.length
        };
      }
    }),
    getLastKnownLocation: builder.mutation<LoadLocation, LastKnownLocationParams>({
      query: (params) => {
        const { truck_id } = params;

        return {
          url: `/equipment/${truck_id}/last-know-location`,
          method: 'POST',
          body: {
            trip_id: params.segment_id,
            start_date: params.start_date,
            start_time: params.start_time
          }
        };
      },
      transformResponse: (response: { data: LoadLocation }) => response.data
    })
  }),
  overrideExisting: false
});

export const {
  useGetEquipmentsQuery,
  useGetEquipmentByIdQuery,
  useAddEquipmentMutation,
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation,
  useGetPublicLoadEquipmentQuery,
  useGetAllEquipmentByTokenQuery,
  usePrefetch: usePrefetchEquipmentQuery,
  useGetLastKnownLocationMutation,

  useSearchEquipmentsQuery,
  useLazySearchEquipmentsQuery,

  util: equipmentApiUtil
} = equipmentApi;
